import useSWR, { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import useSWRImmutable from 'swr/immutable'

import {
  bookmarkVideo,
  getAllVideos,
  getAllVideoTypes,
  getBookmarkedVideos,
  getContinueWatching,
  getHistory,
  getLastVideos,
  getRecommendedVideos,
  getRelatedVideos,
  getVideoById,
  upsertVideoActivity,
} from '../../api/videos'
import { useInvalidateCourseCache }  from './useCourses'

const CACHE_KEYS = {
  BASE: 'videos',
  ALL: 'all',
  LAST: 'last',
  RECOMMENDED: 'recommended',
  RELATED: 'related',
  BOOKMARKED: 'bookmarked',
  CONTINUE_WATCHING: 'continue-watching',
  HISTORY: 'history',
  TYPES: 'types',
} as const

export const useAllVideos = () => {
  return useSWR([CACHE_KEYS.BASE, CACHE_KEYS.ALL], () => getAllVideos())
}

export const useLastVideos = (maxItems: number) => {
  return useSWR([CACHE_KEYS.BASE, CACHE_KEYS.LAST], () => getLastVideos(maxItems))
}

export const useRecommendedVideos = (maxItems: number) => {
  return useSWR([CACHE_KEYS.BASE, CACHE_KEYS.RECOMMENDED], () => getRecommendedVideos(maxItems))
}

export const useRelatedVideos = (videoId: number, maxItems: number) => {
  return useSWR([CACHE_KEYS.BASE, CACHE_KEYS.RELATED, videoId], () => getRelatedVideos(videoId, maxItems))
}

export const useVideoById = (videoId: string) => {
  return useSWR(videoId ? [CACHE_KEYS.BASE, videoId] : null, () => getVideoById(videoId))
}

export const useMutateVideoActivity = (videoId: string) => {
  return useSWRMutation([CACHE_KEYS.BASE, videoId], upsertVideoActivity, {
    // ideal would be to update the cache of the video just mutated
    // but swr doesnt recommend doing so, easiest is to invalidate all keys starting with 'videos'
    onSuccess: async () => {
      await mutate(key => Array.isArray(key) && key[0] === CACHE_KEYS.BASE)
      await useInvalidateCourseCache()
    },
  })
}

export const useMutateBookmarkVideo = (videoId: string) => {
  return useSWRMutation([CACHE_KEYS.BASE, videoId], bookmarkVideo, {
    onSuccess: async () => {
      await mutate([CACHE_KEYS.BASE, CACHE_KEYS.LAST])
      await useInvalidateCourseCache()
    },
  })
}

export const useBookmarkedVideos = () => {
  return useSWR([CACHE_KEYS.BASE, CACHE_KEYS.BOOKMARKED], getBookmarkedVideos)
}

export const useContinueWatchingVideos = (maxItems: number) => {
  return useSWR([CACHE_KEYS.BASE, CACHE_KEYS.CONTINUE_WATCHING, maxItems], () => getContinueWatching(maxItems))
}

export const useHistory = (maxItems?: number) => {
  return useSWR([CACHE_KEYS.BASE, CACHE_KEYS.HISTORY, maxItems], () => getHistory(maxItems))
}

export const useVideoTypes = () => {
  return useSWRImmutable([CACHE_KEYS.BASE, CACHE_KEYS.TYPES], getAllVideoTypes)
}
